import { z } from 'zod/v4'
import { readProperty } from '@/lib/utils'
import {
  salesOrderPrintTemplateValues,
  salesOrderPrintTemplates,
} from '@/lib/services/sales-orders/sales-order-print-templates'
import type {
  SuperTicketAdjustments,
  SuperTicketLayout,
  SuperTicketLayoutInput,
  SuperTicketLimit,
  SuperTicketSectionLayout,
  SuperTicketSectionLayoutInput,
  SuperTicketTemplateSettings,
} from './super-ticket-settings.types'

export function superTicketLayoutSchema(
  metadata: Pick<SuperTicketTemplateSettings, 'limits' | 'sections'>
) {
  const { offset, scale } = metadata.limits
  const adjustments = {
    x: z.number().min(offset.min).max(offset.max),
    y: z.number().min(offset.min).max(offset.max),
    scale: z.number().min(scale.min).max(scale.max),
  }
  const sections = Object.fromEntries(
    Object.entries(metadata.sections).map(([key, section]) => [
      key,
      z.object({
        layout_version: z.union([z.literal(1), z.literal(2)]).optional(),
        ...adjustments,
        ...(section.targets?.logo ? { logo: z.object(adjustments) } : {}),
        ...(section.custom_text
          ? { custom_text: z.string().max(section.custom_text.max_length) }
          : {}),
        fields: z.object(
          Object.fromEntries(Object.keys(section.fields).map((field) => [field, z.boolean()]))
        ),
      }),
    ])
  )
  return z.object({
    layout_version: z.union([z.literal(1), z.literal(2)]).optional(),
    rotation: z.union([z.literal(0), z.literal(180)]),
    paper_has_preprinted_branding: z.boolean(),
    sections: z.object(sections),
  })
}

export function nudgeSuperTicket(
  value: number,
  direction: number,
  limits: SuperTicketLimit
): number {
  return (
    Math.round(Math.min(limits.max, Math.max(limits.min, value + direction * limits.step)) * 100) /
    100
  )
}

/** `legacyPreprinted` is the browser's remembered paper choice from before shop settings. */
export function initialSuperTicketLayout(
  metadata: Pick<
    SuperTicketTemplateSettings,
    'settings' | 'supports_preprinted_branding' | 'version'
  >,
  legacyPreprinted?: boolean
): SuperTicketLayout {
  return {
    ...metadata.settings,
    paper_has_preprinted_branding:
      metadata.supports_preprinted_branding &&
      (metadata.version > 0
        ? metadata.settings.paper_has_preprinted_branding
        : typeof legacyPreprinted === 'boolean'
          ? legacyPreprinted
          : metadata.settings.paper_has_preprinted_branding),
  }
}

export function editableSuperTicketLayout(layout: SuperTicketLayout): SuperTicketLayout {
  const editable = structuredClone(layout)
  // Resolve the inherited baseline so resetting one section never moves its neighbors.
  for (const section of Object.values(editable.sections)) {
    section.layout_version ??= editable.layout_version ?? 2
  }
  // Older preferences blanked the whole branding section. Express that through
  // its field switches so each checkbox remains the single control for its content.
  const branding = editable.sections['shop-branding']
  if (editable.paper_has_preprinted_branding && branding) {
    branding.fields.logo = false
    branding.fields.contact = false
  }
  editable.paper_has_preprinted_branding = false
  return editable
}

const adjustmentKeys = ['x', 'y', 'scale'] as const

function changedAdjustments(
  value: SuperTicketAdjustments,
  saved: SuperTicketAdjustments | undefined
): Partial<SuperTicketAdjustments> {
  const changes: Partial<SuperTicketAdjustments> = {}
  for (const key of adjustmentKeys) {
    if (value[key] !== saved?.[key]) changes[key] = value[key]
  }
  return changes
}

function changedFields(
  value: Record<string, boolean>,
  saved: Record<string, boolean> | undefined
): Record<string, boolean> {
  const changes: Record<string, boolean> = {}
  for (const [field, visible] of Object.entries(value)) {
    if (visible !== saved?.[field]) changes[field] = visible
  }
  return changes
}

const hasEntries = (value: object) => Object.keys(value).length > 0

function changedSection(
  value: SuperTicketSectionLayout,
  saved: SuperTicketSectionLayout | undefined
): SuperTicketSectionLayoutInput {
  const changes: SuperTicketSectionLayoutInput = changedAdjustments(value, saved)
  if (value.layout_version !== undefined && value.layout_version !== saved?.layout_version) {
    changes.layout_version = value.layout_version
  }
  if (value.logo) {
    const logo = changedAdjustments(value.logo, saved?.logo)
    if (hasEntries(logo)) changes.logo = logo
  }
  if (value.custom_text !== undefined && value.custom_text !== saved?.custom_text) {
    changes.custom_text = value.custom_text
  }
  const fields = changedFields(value.fields, saved?.fields)
  if (hasEntries(fields)) changes.fields = fields
  return changes
}

/** Only the settings that differ from `saved`, which is what the API merges. */
function changedSettings(
  value: SuperTicketLayout,
  saved: SuperTicketLayout
): SuperTicketLayoutInput {
  const changes: SuperTicketLayoutInput = {}
  if (value.layout_version !== saved.layout_version) changes.layout_version = value.layout_version
  if (value.rotation !== saved.rotation) changes.rotation = value.rotation
  if (value.paper_has_preprinted_branding !== saved.paper_has_preprinted_branding) {
    changes.paper_has_preprinted_branding = value.paper_has_preprinted_branding
  }
  const sections: Record<string, SuperTicketSectionLayoutInput> = {}
  for (const [key, section] of Object.entries(value.sections)) {
    const sectionChanges = changedSection(section, saved.sections[key])
    if (hasEntries(sectionChanges)) sections[key] = sectionChanges
  }
  if (hasEntries(sections)) changes.sections = sections
  return changes
}

export function superTicketLayoutChanges(
  metadata: Pick<SuperTicketTemplateSettings, 'settings'>,
  layout: SuperTicketLayout
): SuperTicketLayoutInput | null {
  const changes = changedSettings(layout, editableSuperTicketLayout(metadata.settings))
  if (!hasEntries(changes)) return null

  // The first edit converts the old whole-panel switch into individual checkboxes.
  // Preserve unchecked branding fields when the API merges this partial update.
  const branding = layout.sections['shop-branding']
  if (metadata.settings.paper_has_preprinted_branding) {
    changes.paper_has_preprinted_branding = false
    if (branding) {
      changes.sections = {
        ...changes.sections,
        'shop-branding': {
          ...changes.sections?.['shop-branding'],
          fields: branding.fields,
        },
      }
    }
  }
  return changes
}

// The settings API includes the current complete resource when an optimistic save conflicts.
// Validate this unknown error body without replacing the resource or discarding future fields.
const wireAdjustments = z.object({ x: z.number(), y: z.number(), scale: z.number() })
const wireLayout = z.object({
  layout_version: z.union([z.literal(1), z.literal(2)]),
  rotation: z.union([z.literal(0), z.literal(180)]),
  paper_has_preprinted_branding: z.boolean(),
  sections: z.record(
    z.string(),
    wireAdjustments.extend({
      layout_version: z.union([z.literal(1), z.literal(2)]).optional(),
      fields: z.record(z.string(), z.boolean()),
      logo: wireAdjustments.optional(),
      custom_text: z.string().optional(),
    })
  ),
})
const wireLimit = z.object({ min: z.number(), max: z.number(), step: z.number() })
const templateResourceSchema = z.object({
  template: z.enum(
    salesOrderPrintTemplateValues.filter((value) => value !== salesOrderPrintTemplates.OrderSummary)
  ),
  label: z.string(),
  page: z.object({ width: z.number(), height: z.number() }),
  supports_preprinted_branding: z.boolean(),
  sections: z.record(
    z.string(),
    z.object({
      label: z.string(),
      fields: z.record(z.string(), z.string()),
      targets: z.object({ text: z.string(), logo: z.string() }).optional(),
      custom_text: z.object({ max_length: z.number() }).optional(),
    })
  ),
  defaults: wireLayout,
  limits: z.object({ offset: wireLimit, scale: wireLimit }),
  version: z.number(),
  settings: wireLayout,
  updated_at: z.string().nullable(),
  updated_by: z.object({ id: z.number(), name: z.string() }).nullable(),
}) satisfies z.ZodType<SuperTicketTemplateSettings>

function isTemplateResource(value: unknown): value is SuperTicketTemplateSettings {
  return templateResourceSchema.safeParse(value).success
}

export function superTicketConflict(error: unknown): SuperTicketTemplateSettings | null {
  const response = readProperty(error, 'response')
  const current = readProperty(readProperty(response, 'data'), 'current')
  return readProperty(response, 'status') === 409 && isTemplateResource(current) ? current : null
}
